import { flash, FormGroup, Label, SubmitButton, Textarea } from "@xola/ui-kit";
import { FormField, InputField, requiredEmail, requiredString } from "common";
import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { BaseSchedule, Delegate, Guide } from "types";
import { object, string } from "yup";
import { ScrollToFieldError } from "../../../../helpers/validationHelper";
import { useSeller } from "../../../../hooks/useAccount";
import { useInvalidate } from "../../../../hooks/useInvalidation";
import { xolaApiUrl } from "../../../../utils/url";
import { ExternalCalendarLinkModal } from "../../../Common/ExternalCalendarLinkModal";
import { ImageUploadInput } from "../../../Common/ImageUploadInput";
import { SecondaryButton } from "../../../Common/SecondaryButton";
import { XolaFormik } from "../../../Common/XolaFormik";
import { ContentLayout } from "../../../Layout/ContentLayout";
import { GuideSchedule } from "../GuideSchedule";

export interface GuideFormData {
    id?: ID;
    seller: { id: ID };
    user: { email: string };
    name: string;
    phone: string;
    bio?: string;
    schedules?: BaseSchedule[];
    picture?: { id: ID; src: string } | null;
}

const validationSchema = object().shape({
    user: object().shape({
        email: requiredEmail(),
    }),
    seller: object().shape({
        id: requiredString(),
    }),
    name: requiredString(),
    phone: string(),
    bio: string(),
});

const initialValues = (sellerId: ID, guide?: Guide, delegate?: Delegate) => {
    const values: GuideFormData = {
        id: guide?.id,
        user: { email: delegate?.user?.email ?? "" },
        seller: { id: sellerId },
        name: guide?.name ?? "",
        phone: guide?.phone ?? "",
        bio: guide?.bio ?? "",
        schedules: [],
        picture: guide?.picture ?? null,
    };

    return values;
};

interface GuideItemInformationProps {
    guide?: Guide;
    delegate?: Delegate;
    onSubmit: (data: GuideFormData) => void;
    isLoading: boolean;
}

export const InformationTab = ({ guide, delegate, onSubmit, isLoading }: GuideItemInformationProps) => {
    const seller = useSeller();
    const invalidate = useInvalidate();
    const history = useHistory();
    const [syncCalendarOpen, setSyncCalendarOpen] = useState(false);

    const readOnlyEmail = !!delegate?.user?.email;

    // image url' suffix for reloading the browser cache once image is uploaded
    const [pictureSuffix, setPictureSuffix] = useState(Date.now());

    const data = initialValues(seller.id, guide, delegate);

    const handleSubmit = async (values: any) => {
        onSubmit(values);
    };

    const onCancel = () => {
        history.push("/resources/guides");
    };

    const handlePictureUpdated = () => {
        invalidate("guides", guide!.id);
        setPictureSuffix(Date.now());
    };

    const updatePictureUrl = guide ? xolaApiUrl(`/sellers/${seller?.id}/guides/${guide.id}/media`) : undefined;
    const getPictureSource = (pictureId: string | undefined) =>
        pictureId
            ? xolaApiUrl(`/sellers/${seller.id}/guides/${guide?.id}/picture?size=medium&${pictureSuffix}`)
            : undefined;

    return (
        <ContentLayout className="space-y-6">
            <XolaFormik
                enableReinitialize
                validateOnChange
                validateOnBlur={false}
                validationSchema={validationSchema}
                initialValues={data}
                onSubmit={handleSubmit}
            >
                {({ handleSubmit, values, setFieldValue }: any) => (
                    <>
                        <fieldset className="flex-1 space-y-12" disabled={isLoading}>
                            <div className="space-y-4">
                                <h4 className="font-bold">Details</h4>
                                <FormField name="name" label="Name">
                                    <InputField isRequired value={values.name} />
                                </FormField>

                                <FormField name="user.email" label="Email">
                                    <InputField isRequired readOnly={readOnlyEmail} value={values.user.email} />
                                </FormField>

                                <FormField name="phone" label="Phone">
                                    <InputField value={values.phone} />
                                </FormField>

                                <FormGroup>
                                    <Label>Bio</Label>
                                    <Textarea
                                        value={values.bio}
                                        size="large"
                                        rows={6}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                            setFieldValue("bio", e.target.value);
                                        }}
                                    />
                                </FormGroup>
                            </div>

                            {guide?.id && (
                                <>
                                    <div className="space-y-4">
                                        <h4 className="font-bold">Schedule</h4>
                                        <FormGroup>
                                            <GuideSchedule guide={guide} />
                                        </FormGroup>
                                    </div>

                                    <div className="space-y-4">
                                        <h4 className="font-bold">Photo</h4>
                                        <ImageUploadInput
                                            src={getPictureSource(values?.picture?.id)}
                                            hasDelete={!!values?.picture?.id}
                                            uploadUrl={updatePictureUrl ?? ""}
                                            onDelete={() => setFieldValue("picture", null)}
                                            onUpdated={handlePictureUpdated}
                                            onError={(error: string) => flash.danger(error)}
                                        />
                                    </div>

                                    <div className="flex items-center justify-between">
                                        <h4 className="font-bold">External calendar link</h4>
                                        <SecondaryButton
                                            size="small"
                                            onClick={() => {
                                                setSyncCalendarOpen(true);
                                            }}
                                        >
                                            Click to copy
                                        </SecondaryButton>
                                    </div>
                                </>
                            )}
                        </fieldset>

                        <ContentLayout.Footer>
                            <SubmitButton type="submit" isLoading={isLoading} onClick={handleSubmit}>
                                Save
                            </SubmitButton>

                            <SecondaryButton type="reset" onClick={onCancel}>
                                Cancel
                            </SecondaryButton>
                        </ContentLayout.Footer>

                        <ScrollToFieldError />
                    </>
                )}
            </XolaFormik>

            <ExternalCalendarLinkModal
                isOpen={syncCalendarOpen}
                userId={delegate?.user.id}
                onClose={() => setSyncCalendarOpen(false)}
            />
        </ContentLayout>
    );
};
